import { Banner, Screenshot } from '@apis/index'
import {
  deletesvg,
  edit,
  outlinenotification,
  switchOff,
  switchOn,
} from '@assets/icons'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import Svg from '@components/svg'
import Table from '@components/table'
import Wrapper from '@components/wrapper'
import useScrollToTop from '@hooks/useScrollToTop'
import { Actions, RootState } from '@store/index'
import theme from '@utils/theme'
import React, { Fragment, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, ArrowRight, ImagePlus } from 'lucide-react'
import { useModal } from '@context/ModalContext'
import {
  Body,
  Button,
  Container,
  FileInput,
  Footer,
  Header,
  ImagePreview,
  UploadArea,
  UploadLabel,
} from './style'
import screenshot from '@apis/screenshot'

const ScreenshotList = () => {
  const { showModal } = useModal()
  const dispatch = useDispatch()
  const scrollToTop = useScrollToTop()
  const navigate = useNavigate()
  const {
    items: screenshot,
    totalItems,
    totalPages,
    loading,
  } = useSelector((state: RootState) => state.screenshot)
  const [page, setPage] = useState(1)

  const fetchScreenshots = async () => {
    try {
      await Screenshot.list({
        state: 'screenshot',
        params: { page, limit: 30 },
      })
    } catch (error) {
      console.error('Failed to fetch screenshots', error)
    }
  }

  const columns = [
    {
      title: 'SN',
      key: 'sn',
      render: (_, i) => <p>{i}</p>,
    },
    {
      title: 'Screenshot Image',
      key: 'image',
      render: (item) => {
        const images = item.images
        if (images.length === 0) {
          return <span>No images</span>
        }

        const settings = {
          dots: false,
          infinite: images.length > 1,
          speed: 300,
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: true,
          adaptiveHeight: true,
          nextArrow: <ArrowLeft />,
          prevArrow: <ArrowRight />,
        }
        return (
          <div
            style={{
              width: '120px',
              maxWidth: '100%',
              margin: '0 auto',
              padding: '4px 0',
            }}
          >
            <Slider {...settings}>
              {images.map((img, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '70px',
                  }}
                >
                  <img
                    src={`https://admin.tournaop.com/${img}`}
                    alt={`ref-${idx}`}
                    style={{
                      width: '100px',
                      height: '100px',
                      objectFit: 'contain',
                      borderRadius: '4px',
                      margin: '0 auto',
                      display: 'block',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                      background: '#f8f8f8',
                    }}
                  />
                </div>
              ))}
            </Slider>
            <style>{`
            .slick-dots li button:before {
              color: #333 !important;
              font-size: 10px !important;
              opacity: 0.7;
            }
            .slick-dots li.slick-active button:before {
              color: #1976d2 !important;
              opacity: 1;
            }
            .slick-arrow {
              // z-index: 9999999999;
             
            }
          `}</style>
          </div>
        )
      },
    },
    {
      title: 'Type',
      key: 'type',
      render: (item) => <p>{item.type}</p>,
    },
    {
      title: 'Game',
      key: 'game',
      render: (item) => <p>{item.game}</p>,
    },
    {
      title: 'Description',
      key: 'description',
      render: (item) => <p>{item.description}</p>,
    },
    {
      title: 'Status',
      key: 'status',
      render: (item) => (
        <Svg
          src={item.status ? switchOn : switchOff}
          onClick={async () => {
            console.log(item)
            await Screenshot.update({
              id: item.id,
              state: 'screenshot',
              data: { status: !item.status },
            })
          }}
        />
      ),
    },
  ]

  const rowData = screenshot?.map((item) => ({
    id: item.id,
    images: item.images,
    type: item.type,
    game: item.gameType,
    description: item.description,
    status: item.status,
  }))

  useEffect(() => {
    fetchScreenshots()
  }, [page])

  return (
    <Fragment>
      <Wrapper>
        <Table
          loading={loading}
          searchKeys={['type', 'description']}
          text="Screenshot References List"
          icon={outlinenotification}
          columns={columns}
          data={rowData}
          pageControlled={page}
          onPageChange={setPage}
          totalItems={totalItems}
          totalPages={totalPages}
          actions={[
            {
              icon: edit,
              key: 'edit',
              onClick: (item) => {
                navigate(
                  `/admin/banner-settings/${item.id}?tab=Screenshot References`,
                  { state: item },
                )
                scrollToTop()
              },
            },
            {
              icon: deletesvg,
              key: 'remove',
              onClick: async (item) =>
                await Screenshot.remove({ state: 'screenshot', id: item.id }),
            },
            {
              icon: <ImagePlus />,
              key: 'edit',
              onClick: async (item) => {
                showModal(<AddImageModal item={item} />)
              },
            },
          ]}
        />
      </Wrapper>
    </Fragment>
  )
}

export default ScreenshotList

export const AddImageModal = ({ item }: any) => {
  const { hideModal } = useModal()
  const [imageUrl, setImageUrl] = useState('')
  const [isUpdating, setIsUpdating] = useState(false)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (event) => {
        setImageUrl(event.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleUpdate = async () => {
    setIsUpdating(true)
    const updateData = {
      type: item.type,
      description: item.description,
      role: item.userRole,
      images: [
        ...(item.images || []),
        {
          base64: imageUrl.split(',')[1] || '',
          extension: imageUrl.split(';')[0].split('/')[1] || '',
        },
      ],
    }

    await screenshot.update({
      state: 'screenshot',
      id: item.id,
      data: updateData,
    })
    hideModal()
  }

  return (
    <Container>
      <Header>
        <h3>Add Image for screenshot reference</h3>
      </Header>

      <Body>
        {imageUrl ? (
          <ImagePreview src={imageUrl} alt="Preview" />
        ) : (
          <UploadArea>
            <FileInput
              type="file"
              id="image-upload"
              accept="image/*"
              onChange={handleFileChange}
            />
            <UploadLabel htmlFor="image-upload">
              <span>📁</span>
              <p>Click to upload image</p>
            </UploadLabel>
          </UploadArea>
        )}
      </Body>

      <Footer>
        <Button onClick={handleUpdate} disabled={!imageUrl || isUpdating}>
          {isUpdating ? 'Updating...' : 'Update'}
        </Button>
      </Footer>
    </Container>
  )
}
